import * as React from 'react'

import CategoryIcon from '@/assets/food/icons/category.svg?react'
import CategoryDefault from '@/assets/food/icons/categoryDefault.svg?react'
import PlusIcon from '@/assets/food/icons/plus.svg?react'
import StoreDefault from '@/assets/food/icons/storeDefault.svg?react'
import ChevronLeft from '@/assets/icons/chevron-left.svg?react'
import CloseIcon from '@/assets/icons/close.svg?react'
import SearchIcon from '@/assets/icons/search.svg?react'
import StarIcon from '@/assets/icons/star/full.svg?react'
import Loading from '@/components/Loading/Loading'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import type {Category, MenuItemList, Quote, StoreIdProps} from '@/lib/food/foodModel'
import HeadTags from '@/lib/head/HeadTags'
import {FoodRoute, StoreDetailRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import CategoryItemPrice from './components/CategoryItemPrice'
import ReadMoreList from './components/ReadMoreList'
import ReadMoreString from './components/ReadMoreString'
import style from './FoodStore.module.scss'
import FoodStoreCategoryDetail from './FoodStoreCategoryDetail'

const ImageDefaultLoader = ({value}: {value: 'store' | 'category'}) => {
  return (
    <div className={style.StoreImageDefaultContainer}>
      {value === 'store' ? <StoreDefault /> : <CategoryDefault />}
    </div>
  )
}

interface FoodStoreDetailProps {
  isLoading: boolean
  isError: boolean
  storeId: StoreIdProps
  store: Category[]
  quote: Quote
  deliveryType: boolean
  changeDeliverytype?: (value: boolean) => void
}

export default memo(function FoodStoreUI(props: FoodStoreDetailProps) {
  const {storeId, store, deliveryType, quote} = props

  const router = useRouter()
  const [filterInput, setFilterInput] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState<MenuItemList | null>(null)

  const {categoryId} = StoreDetailRoute.useSearch()

  const refs = useRef<(HTMLDivElement | null)[]>([])
  const filterInputRef = useRef<HTMLInputElement>(null)

  const storeAddressFormat = `${props.storeId.store_address.street_addr} ${props.storeId.store_address.street_addr_2}, ${props.storeId.store_address.city}, ${props.storeId.store_address.state}, ${props.storeId.store_address.country}`

  const handleClick = (index: number) => {
    setActiveIndex(index)
    refs.current[index]?.scrollIntoView({behavior: 'smooth', block: 'start'})
  }

  const getGoBackHref = useCallback(
    (
      lng: number,
      lat: number,
      address: string,
      user_street_num: string,
      user_street_name: string,
      user_city: string,
      user_state: string,
      user_zipcode: string,
      user_country: string,
    ) => {
      return router.buildLocation({
        to: FoodRoute.fullPath,
        search: {
          lng,
          lat,
          address,
          user_street_num,
          user_street_name,
          user_city,
          user_state,
          user_zipcode,
          user_country,
        },
      }).href
    },
    [router],
  )

  // const handleChangeDeliveryType = (value: boolean) => {
  //   if (changeDeliverytype) {
  //     changeDeliverytype(value)
  //   }
  // }

  const filteredMeals = store
    .map(category => {
      const matchedCategoryItem = category.menu_item_list.filter(item =>
        item.name.toLowerCase().includes(filterInput.toLowerCase()),
      )
      if (matchedCategoryItem.length > 0) {
        return {...category, menu_item_list: matchedCategoryItem}
      }
      return null
    })
    .filter(Boolean)

  const getCategoryDetailHref = useCallback(
    (categoryId: string) => {
      return router.buildLocation({
        to: StoreDetailRoute.fullPath,
        search: {
          categoryId,
        },
      }).href
    },
    [router],
  )

  const getCategoryDetail = (category: MenuItemList) => {
    getCategoryDetailHref(category.product_id)
    setSelectedCategory(category)
  }

  return (
    <div>
      <HeadTags title={storeId.store_name} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar withSearch variant='dark' />
      <main className={style.PageContainer} {...skipTargetProps('main-content')}>
        {!categoryId && (
          <>
            <section className={style.PageHeader}>
              <div className={style.PageHeaderContent}>
                <Link
                  to={getGoBackHref(
                    storeId.lng,
                    storeId.lat,
                    storeId.address,
                    storeId.user_street_num,
                    storeId.user_street_name,
                    storeId.user_city,
                    storeId.user_state,
                    storeId.user_zipcode,
                    storeId.user_country,
                  )}
                  aria-label='Go back to store search detail page'
                  className={style.ButtonBack}
                >
                  <ChevronLeft className={style.ButtonBackIcon} />
                  <p className={style.ButtonBackTitleMobile}>Go back</p>
                </Link>
                <div className={style.HeaderImageContainer}>
                  <div className={style.HeaderImageContent}>
                    {storeId.store_image.length > 0 ? (
                      <>
                        {storeId.store_image.map(image => (
                          <img
                            src={image}
                            alt=''
                            key={image}
                            loading='lazy'
                            className={style.HeaderImage}
                          />
                        ))}
                      </>
                    ) : (
                      <ImageDefaultLoader value='store' />
                    )}
                  </div>
                  <div className={style.HeaderImageInfoMobile}>
                    <h1 className={style.Heading}>{storeId.store_name}</h1>
                    <ReadMoreList
                      items={storeId.store_cuisine}
                      maxVisible={10}
                      viewMoreTitle='Show more'
                      viewLessTitle='Show less'
                    />
                  </div>
                </div>
                <div className={style.HeaderInfoContainer}>
                  <div className={style.HeaderInfoDesktop}>
                    <h1 className={style.Heading}>{storeId.store_name}</h1>
                    <ReadMoreList
                      items={storeId.store_cuisine}
                      maxVisible={10}
                      viewMoreTitle='Show more'
                      viewLessTitle='Show less'
                    />
                  </div>
                  <div className={style.HeaderInfoDetail}>
                    {!!storeId.store_description && (
                      <div className={style.Description}>
                        <VisuallyHidden>Store detail description:</VisuallyHidden>
                        <ReadMoreString text={storeId.store_description} maxChars={150} />
                      </div>
                    )}
                    <div className={style.BreakLine}></div>
                    {storeId.store_rating && (
                      <div className={style.RatingContainer}>
                        <VisuallyHidden>{`Rating: ${storeId.store_rating.toFixed(1)} out of 5 stars based on ${storeId.store_rating_count} reviews`}</VisuallyHidden>
                        <div className={style.RatingNumber} aria-hidden='true'>
                          {storeId.store_rating.toFixed(1)}
                        </div>
                        <StarIcon />
                        <div className={style.RatingNumber} aria-hidden='true'>
                          {'\u0028'}
                          {storeId.store_rating_count} reviews
                          {'\u0029'}
                        </div>
                      </div>
                    )}
                    <div className={style.Address}>
                      <VisuallyHidden>Store address: </VisuallyHidden> {storeAddressFormat}
                    </div>
                    <div className={style.DeliveryInfoContainer}>
                      {/* <div className={style.DeliveryItem}>
                        <VisuallyHidden>Delivery time: </VisuallyHidden>
                        {`${storeId.store_delivery_time} mins`}
                      </div>
                      <div className={style.DeliveryBreakLine} aria-hidden='true'></div> */}
                      <div className={style.DeliveryItem}>
                        <VisuallyHidden>Delivery distance: </VisuallyHidden>
                        {`\u003C ${storeId.store_delivery_distance?.toFixed(1)} mi`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {props.isLoading && (
              <section className={style.PageContent}>
                <Loading label='Loading store detail' />
              </section>
            )}
            {!props.isLoading && (
              <section className={style.PageContent}>
                <div className={style.CategoryContainer}>
                  <div className={style.CategoryOptionContainer}>
                    <div className={style.CategoryOption}>
                      <CategoryIcon className={style.Icon} />
                      <h2 className={style.Heading}>Categories</h2>
                    </div>
                    <div className={style.CategoryList} role='list'>
                      {store.map((meal, index) => (
                        <button
                          key={meal.name}
                          type='button'
                          onClick={() => {
                            handleClick(index)
                          }}
                          className={clsx(style.Item, activeIndex === index && style.ItemActive)}
                        >
                          <VisuallyHidden role='heading' aria-level={3}></VisuallyHidden>
                          {meal.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className={style.CategoryDetailContainer}>
                    <div className={style.CategoryDetailFilter}>
                      <div className={style.FilterContainer}>
                        <div className={style.FilterSearchContainer}>
                          <SearchIcon className={style.Icon} />
                          <input
                            ref={filterInputRef}
                            type='text'
                            placeholder='Filter result'
                            value={filterInput}
                            onChange={e => {
                              setFilterInput(e.target.value)
                            }}
                            className={style.SearchBox}
                          />
                          {filterInput.length > 0 && (
                            <button
                              type='button'
                              onClick={() => {
                                setFilterInput('')
                              }}
                              className={style.SearchBoxClearButtonContainer}
                            >
                              <CloseIcon className={style.SearchBoxClearButtonIcon} />
                            </button>
                          )}
                        </div>
                        <div className={style.FilterButtonContainer}>
                          {storeId.delivery_enabled && (
                            <div
                              // onClick={() => {
                              //   handleChangeDeliveryType(false)
                              // }}
                              className={clsx(
                                style.FilterButton,
                                // !deliveryType && style.FilterButtonActive,
                              )}
                            >
                              <p className={style.ButtonTitle}>Delivery time</p>
                              {!deliveryType && (
                                <div className={style.ButtonValue}>
                                  {quote.time_estimate.minimum} - {quote.time_estimate.maximum} mins
                                </div>
                              )}
                            </div>
                          )}
                          {/* {storeId.pickup_enabled && (
                          <button
                            type='button'
                            onClick={() => {
                              handleChangeDeliveryType(true)
                            }}
                            className={clsx(
                              style.FilterButton,
                              deliveryType && style.FilterButtonActive,
                            )}
                          >
                            <p className={style.ButtonTitle}>Pickup</p>
                            {deliveryType && (
                              <div className={style.ButtonValue}>
                                {quote.time_estimate.minimum} - {quote.time_estimate.maximum} mins
                              </div>
                            )}
                          </button>
                        )} */}
                        </div>
                      </div>
                      <div className={style.CategoryOptionMobile}>
                        <div className={style.CategoryList} role='list' aria-label='List of categories'>
                          {store.map((meal, index) => (
                            <button
                              key={meal.name}
                              type='button'
                              onClick={() => {
                                handleClick(index)
                              }}
                              className={clsx(
                                style.Item,
                                activeIndex === index && style.ItemActive,
                              )}
                            >
                              <VisuallyHidden role='heading' aria-level={3}></VisuallyHidden>
                              {meal.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    {filteredMeals.length > 0 ? (
                      <div className={style.CategoryDetailContent} role='list'>
                        {filteredMeals.map((item, index) => (
                          <div
                            key={item.name}
                            ref={el => {
                              refs.current[index] = el
                            }}
                            className={style.CategoryItem}
                            role='listitem'
                          >
                            <div className={style.CategoryItemHeading}>
                              <VisuallyHidden>Category:</VisuallyHidden>
                              {item.name}
                            </div>
                            <div className={style.ItemList} role='list'>
                              {item.menu_item_list.map(item => {

                                const dollarPrice = item.min_price/100

                                return (
                                  <div
                                    role='listitem'
                                    key={item.product_id}
                                    className={style.Item}
                                  >
                                    <div className={style.ItemInfo}>
                                      <div className={style.ItemInfoHeader}>
                                        <h3 className={style.ItemTitle}>{item.name}</h3>
                                        <div className={style.ItemDesc}>
                                          <VisuallyHidden>
                                            Description:{' '}
                                            {item.description.length === 0 && 'Not available'}
                                          </VisuallyHidden>
                                          <ReadMoreString text={item.description} maxChars={100} />
                                        </div>
                                      </div>
                                      <div className={style.ItemBasePrice}>
                                        <CategoryItemPrice price={dollarPrice}/>
                                      </div>
                                    </div>
                                    <div className={style.ItemImageContainer}>
                                      {item.image ? (
                                        <img
                                          src={item.image}
                                          alt=''
                                          loading='lazy'
                                          className={style.Image}
                                        />
                                      ) : (
                                        <ImageDefaultLoader value='category' />
                                      )}
                                      <div className={style.AddingButtonContainer}>
                                        <Link
                                          to={getCategoryDetailHref(item.product_id)}
                                          aria-label={`Add ${item.name} to cart`}
                                          className={style.AddingButton}
                                          onClick={() => {
                                            getCategoryDetail(item)
                                          }}
                                        >
                                          <PlusIcon className={style.AddingButtonIcon} />
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={style.CategoryDetailEmpty}>
                        <VisuallyHidden>No items matching: {filterInput}</VisuallyHidden>
                        No items matching: {filterInput}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
        {categoryId && selectedCategory && (
          <FoodStoreCategoryDetail categoryDetail={selectedCategory} storeId={storeId} />
        )}
      </main>
    </div>
  )
})
